export interface FormatFixedOptions {
  /** Decimal places. Defaults to 2. */
  decimals?: number;
}

/**
 * Formats a number with fixed decimal places and no thousand separators
 * (e.g. 8 → "8.00", 1000 → "1000.00"). Null/undefined are treated as 0.
 * @registry Canonical fixed-decimal formatter; use instead of `.toFixed()` in JSX.
 */
export const formatFixed = (
  value?: number | null,
  options: FormatFixedOptions = {},
): string => (value ?? 0).toFixed(options.decimals ?? 2);

export type FormatHoursOptions = FormatFixedOptions;

/**
 * Formats a number of hours with fixed decimal places and no thousand
 * separators. Mirrors the long-standing `value.toFixed(2)` behavior in report
 * tables.
 * @registry Canonical hours formatter (fixed decimals, no thousand separators); use instead of `.toFixed()` in JSX.
 */
export const formatHours = formatFixed;

// Cached Intl formatters: construction is non-trivial and these run per-cell
// in stat tiles and admin tables.
const numberFormatter = new Intl.NumberFormat('en-US');

const compactHoursFormatter = new Intl.NumberFormat('en-US', {
  minimumFractionDigits: 0,
  maximumFractionDigits: 2,
  useGrouping: false,
});

/**
 * Formats a number of hours with decimals only where they carry information
 * (8 → "8", 1.5 → "1.5", 1.25 → "1.25") and no thousand separators.
 * Null/undefined are treated as 0.
 * @registry Canonical compact hours formatter (trailing zeros dropped); use where fixed 2-decimal `formatHours` reads as noise.
 */
export const formatHoursCompact = (value?: number | null): string =>
  compactHoursFormatter.format(value ?? 0);

/**
 * Formats a number with thousand separators and no decimals (e.g. 12345 →
 * "12,345"). Returns "N/A" for null/undefined.
 * @registry Canonical thousands-separated integer formatter; use instead of `toLocaleString()`.
 */
export const formatNumber = (value?: number | null): string => {
  if (value === undefined || value === null) return 'N/A';
  return numberFormatter.format(value);
};

const percentFormatter = new Intl.NumberFormat('en-US', {
  style: 'percent',
  maximumFractionDigits: 1,
});

export interface FormatPercentOptions {
  /** Decimal places. Defaults to 1. */
  fractionDigits?: number;
  /** Returned for null/undefined. Defaults to 'N/A'. */
  nullText?: string;
}

/**
 * Formats a 0-1 fraction as a percentage string (e.g. 0.123 → "12.3%").
 * Returns `nullText` ('N/A' by default) for null/undefined.
 * @registry Canonical fraction-to-percent formatter; use instead of hand-rolled `* 100` + toFixed.
 */
export const formatPercent = (
  value?: number | null,
  options: FormatPercentOptions = {},
): string => {
  if (value === undefined || value === null) return options.nullText ?? 'N/A';
  const formatter =
    options.fractionDigits === undefined
      ? percentFormatter
      : new Intl.NumberFormat('en-US', {
          style: 'percent',
          maximumFractionDigits: options.fractionDigits,
        });
  return formatter.format(value);
};

const FILE_SIZE_UNITS = ['B', 'KB', 'MB', 'GB', 'TB'];

/**
 * Formats a byte count with the largest unit that keeps it above 1 (e.g. 2500 →
 * "2.44 KB"), trimming trailing zeros. Missing, non-finite and non-positive
 * sizes render as "0 B".
 * @registry Canonical file-size formatter; use instead of hand-rolled byte math.
 */
export const humanFileSize = (size?: number | null): string => {
  if (size == null || !Number.isFinite(size) || size <= 0) return '0 B';
  const exponent = Math.min(
    Math.floor(Math.log(size) / Math.log(1024)),
    FILE_SIZE_UNITS.length - 1,
  );
  const value = size / Math.pow(1024, exponent);
  return `${Number(value.toFixed(2))} ${FILE_SIZE_UNITS[exponent]}`;
};
